/**
 * Milo follows the cursor — the part that is about the POINTER rather than about a
 * renderer.
 *
 * # What this is now
 *
 * One measurement and two answers. The measurement is where the cursor is relative to
 * THIS head's centre, normalised and saturated to −1…1 on each axis. The two answers are:
 *
 *   - `headTurnFor()`  — how far the model root is turned, in degrees;
 *   - `eyeWeightsFor()` — the eye morph weights that point the eyes there.
 *
 * Until v1.456.0 there was only the first, and it was not even that: the head was inside
 * a vendored `<model-viewer>` which exposes no bones, no morph targets and no eyes, so
 * the only thing that could be turned was the CAMERA, and a camera moved right shows the
 * left cheek. The model now carries twelve real eye morphs and we draw it ourselves
 * (`libs/miloRenderer.ts`), so the eyes do most of the looking and the head only leans —
 * which is what a person does, and why the head's own limits came DOWN when the eyes
 * arrived.
 *
 * # Why the offset is shared
 *
 * The eyes and the head must agree. Two curves, two reach lengths or two idle timers
 * would be a face whose eyes are already at the corner while the head is still starting,
 * for no reason anybody asked for. There is one saturating curve, one reach, one settle
 * timer, and both outputs are functions of its result.
 *
 * # What it is not
 *
 * It does not touch the animation clips and does not know they exist. A reaction —
 * Success, Frustrated, Smile — animates the FACE; the gaze goes on aiming while it plays.
 */
import { onBeforeUnmount } from 'vue'

import type { MiloEyeWeights } from '@/libs/miloEyes'

/**
 * How far the head may lean, in degrees off the pose the camera frames.
 *
 * Half of what it was. Until the eye morphs arrived the head was the only thing that
 * could point at anything and had to carry the whole gesture (±14° / ±8°); now it is the
 * smaller half of a face whose eyes move first. Small also because Milo is drawn in a
 * 40 px circle that crops to the face (`MiloHead.vue`, `HEAD_SCALE`) — a lean big enough
 * to be dramatic swings the crown and the ear out of the circle instead.
 */
const YAW_DEGREES   = 8
const PITCH_DEGREES = 5

/**
 * The distance at which the gaze is as far over as it goes.
 *
 * A fraction of the viewport was the other candidate and is worse: the same gesture would
 * mean different things on a laptop and on the 27-inch screen the desk actually has, and
 * on the wide one Milo would barely move for a cursor at the far edge. A length is the
 * honest unit — the cursor is 600 px away or it is not.
 */
const REACH_PX = 600

/** How long Milo keeps looking at a cursor that has stopped. */
const SETTLE_MS = 2500

/**
 * Where the gaze is switched off entirely.
 *
 * Reduced motion for the obvious reason, and a coarse pointer because there is no cursor
 * to follow: an iPad reports the last place a finger touched, so a face that followed it
 * would hold a stare at wherever the desk last pressed and never let go. Both are watched
 * live — somebody who turns reduced motion on wants it on now.
 */
const NO_GAZE = '(prefers-reduced-motion: reduce), (pointer: coarse)'

/** Fast where the cursor is near, gently saturating at the limit. */
const ease = (n: number): number => Math.sign(n) * Math.sin(Math.min(Math.abs(n), 1) * Math.PI / 2)

/**
 * Where the cursor is, seen from one head: −1 (left / above) … 1 (right / below), and
 * `{ x: 0, y: 0 }` for "nothing to look at".
 */
export interface MiloGazeOffset {
    x: number
    y: number
}

/** Straight ahead. The value every "there is no cursor" answer returns. */
export const MILO_GAZE_CENTRE: MiloGazeOffset = { x: 0, y: 0 }

/**
 * How far the model root leans, in degrees.
 *
 * The model faces +Z, towards the camera, so a turn about +Y takes the nose towards +X,
 * which is the VIEWER's right — the same side a positive `x` means. A turn about +X takes
 * the nose down, which is what a positive `y` (a cursor below the head) asks for. Both
 * signs are therefore plain, which they were emphatically not while the camera was doing
 * the work.
 */
export const headTurnFor = (offset: MiloGazeOffset): { yaw: number, pitch: number } => ({
    yaw:   offset.x * YAW_DEGREES,
    pitch: offset.y * PITCH_DEGREES,
})

/**
 * The eye morph weights that point both eyes at the cursor.
 *
 * `Left` and `Right` are MILO's own sides (`libs/miloEyes.ts`), so the horizontal pair is
 * crossed: a cursor on the VIEWER's right is towards MILO's LEFT, which is his left eye
 * looking OUT and his right eye looking IN. The package's INTEGRATION.md says exactly
 * this, and it is the one mapping in the file that cannot be reasoned out from the names.
 *
 * The opposite direction of a pair is always written as 0 rather than left alone: the
 * model's own limits forbid opposing looks on one eye, and the only way to be sure of
 * that is to write both ends of every pair on every frame.
 */
export const eyeWeightsFor = (offset: MiloGazeOffset): MiloEyeWeights => {
    const horizontal = Math.abs(offset.x)
    const vertical   = Math.abs(offset.y)

    // Towards MILO's left is the viewer's right.
    const toHisLeft  = offset.x > 0 ? horizontal : 0
    const toHisRight = offset.x < 0 ? horizontal : 0
    const up         = offset.y < 0 ? vertical : 0
    const down       = offset.y > 0 ? vertical : 0

    return {
        eyeLookOutLeft:   toHisLeft,
        eyeLookInRight:   toHisLeft,
        eyeLookOutRight:  toHisRight,
        eyeLookInLeft:    toHisRight,
        eyeLookUpLeft:    up,
        eyeLookUpRight:   up,
        eyeLookDownLeft:  down,
        eyeLookDownRight: down,
    }
}

export interface MiloGaze {
    /** Measure from this element's box. Called again for a head that was re-created. */
    follow(host: HTMLElement | null): void
    /** Stop following. The next `offset()` is the centre. */
    release(): void
    /** Where the cursor is now, saturated. Read once per rendered frame. */
    offset(): MiloGazeOffset
}

/**
 * One gaze per mounted head.
 *
 * Both heads on the page (the top bar and the drawer's header) run their own, because
 * each measures from its OWN centre: the same cursor is up and to the left of one and
 * straight ahead of the other, and a shared computation would have them looking at the
 * same point rather than at the same cursor. The cost is one passive `pointermove`
 * listener each.
 *
 * The listener stores raw coordinates and nothing else; the box is measured in `offset()`,
 * which the renderer calls once per frame it draws. So there is exactly one layout read
 * per drawn frame, and none at all while the head is paused.
 */
export const useMiloGaze = (): MiloGaze => {
    if (typeof window === 'undefined') {
        return { follow: () => {}, release: () => {}, offset: () => MILO_GAZE_CENTRE }
    }

    const blocked = window.matchMedia(NO_GAZE)

    let host:    HTMLElement | null = null
    let settle:  ReturnType<typeof setTimeout> | null = null
    let pointer: { x: number, y: number } | null = null
    let bound    = false

    const cancelSettle = () => {
        if (settle !== null) clearTimeout(settle)

        settle = null
    }

    const forget = () => {
        cancelSettle()
        pointer = null
    }

    const onMove = (event: PointerEvent) => {
        pointer = { x: event.clientX, y: event.clientY }

        cancelSettle()
        // A cursor that has stopped is not a cursor anybody is pointing with, and a face
        // frozen mid-turn looks stuck rather than attentive.
        settle = setTimeout(forget, SETTLE_MS)
    }

    /** The cursor left the page, or the window lost focus: there is nothing to look at. */
    const onLeave = () => forget()

    const listen = () => {
        if (bound) return

        bound = true
        document.addEventListener('pointermove', onMove, { passive: true })
        document.addEventListener('pointerleave', onLeave)
        window.addEventListener('blur', onLeave)
    }

    const silence = () => {
        if (!bound) return

        bound = false
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerleave', onLeave)
        window.removeEventListener('blur', onLeave)
    }

    const onBlockedChange = () => {
        if (blocked.matches) {
            silence()
            forget()
        } else if (host) {
            listen()
        }
    }

    blocked.addEventListener('change', onBlockedChange)

    const release = () => {
        silence()
        forget()
        host = null
    }

    onBeforeUnmount(() => {
        release()
        blocked.removeEventListener('change', onBlockedChange)
    })

    return {
        follow: (element: HTMLElement | null) => {
            host = element

            if (!element) {
                silence()

                return
            }

            if (!blocked.matches) listen()
        },

        release,

        offset: (): MiloGazeOffset => {
            if (!pointer || !host?.isConnected) return MILO_GAZE_CENTRE

            // The element's box is the STAGE, which is larger than the visible circle and
            // sits high in it (`MiloHead.vue`, `HEAD_RISE`) — so its centre is a few
            // pixels above the circle's. That is the right few pixels: the stage is
            // placed so that the FACE lands in the circle, and the face is what is being
            // asked to look.
            const box = host.getBoundingClientRect()

            if (box.width === 0 || box.height === 0) return MILO_GAZE_CENTRE

            return {
                x: ease((pointer.x - (box.left + box.width  / 2)) / REACH_PX),
                y: ease((pointer.y - (box.top  + box.height / 2)) / REACH_PX),
            }
        },
    }
}
